import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  CartAddPendingEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  startCartLinesChange,
  resolveCartLinesUpdate,
  rejectCartLinesUpdate,
  cartLinesContextFor,
  fetchAjaxCart,
} from '@theme/standard-events-dispatch';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange.bind(this), 300);

  /** @type {AbortController} */
  #pendingAbortController = new AbortController();

  /** True while the last line is collapsing before empty chrome. */
  #emptyRevealPending = false;

  /** @type {string | null} */
  #stashedSectionHtml = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);

    this.#pendingAbortController.abort();
    this.#pendingAbortController = new AbortController();
    const { signal } = this.#pendingAbortController;
    document.addEventListener(CartAddPendingEvent.eventName, this.#handleAddPending, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleAddError, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    this.#pendingAbortController.abort();
  }

  /**
   * Qty 0 removes immediately (same path as `.cart-items__remove`). Cancel a pending
   * 2→1 so it cannot apply after the row starts collapsing.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    if (event.detail.quantity === 0) {
      this.#debouncedOnChange.cancel();
      this.#onQuantityChange(event);
      return;
    }

    this.#debouncedOnChange(event);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const revealEmpty = () => {
      if (!isEmptyCart) return;

      if (this.#stashedSectionHtml) {
        this.#emptyRevealPending = false;
        if (this.isDrawer) this.closest('dialog')?.classList.add('cart-drawer--empty');
        this.#applyStashedSection();
        return;
      }

      // Failed `cart/change` already restored server HTML; do not paint empty chrome.
      if (!this.#emptyRevealPending) return;

      this.#emptyRevealPending = false;
      if (this.isDrawer) this.closest('dialog')?.classList.add('cart-drawer--empty');

      const template = document.getElementById('empty-cart-template');
      if (template instanceof HTMLTemplateElement) {
        this.replaceChildren(document.importNode(template.content, true));
      }
    };

    if (isEmptyCart) this.#emptyRevealPending = true;

    if (prefersReducedMotion()) {
      rowsToRemove.forEach((row) => row.remove());
      if (isEmptyCart) revealEmpty();
      return;
    }

    let remaining = rowsToRemove.length;
    rowsToRemove.forEach((row) => {
      const onDone = () => {
        row.remove();
        remaining -= 1;
        if (remaining > 0) return;
        if (isEmptyCart) revealEmpty();
      };

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, onDone);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const lineId = lineItemRow?.dataset.key || String(line);
    const cartLinesDeferred = startCartLinesChange({
      element: this,
      action: quantity === 0 ? 'remove' : 'update',
      context: cartLinesContextFor(this),
      lines: [{ id: lineId, quantity }],
      detail: { source: 'cart-items-component' },
    });

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => this.#parseCartResponse(response))
      .then(async (parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          const ajaxCart = await fetchAjaxCart().catch(() => null);
          void resolveCartLinesUpdate(cartLinesDeferred, {
            ajaxCart,
            userErrors: [{ message: String(parsedResponseText.errors), code: 'INVALID' }],
            detail: { source: 'cart-items-component', didError: true },
          });

          const wasEmptyReveal = this.#emptyRevealPending;
          this.#clearEmptyRevealStash();
          if (wasEmptyReveal) {
            void this.#rerenderSectionFromServer();
            return;
          }
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        void resolveCartLinesUpdate(cartLinesDeferred, {
          ajaxCart: parsedResponseText,
          detail: {
            source: 'cart-items-component',
            itemCount: newCartItemCount,
            sections: parsedResponseText.sections,
            didError: false,
          },
        });

        this.#applySectionHtml(parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        rejectCartLinesUpdate(cartLinesDeferred, error, this);
        this.#clearEmptyRevealStash();
        // Re-render from server state to recover from optimistic UI changes when request/parsing fails.
        void this.#rerenderSectionFromServer();
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Parses cart response payload and validates expected JSON shape.
   * @param {Response} response - Fetch response from cart change endpoint.
   * @returns {Promise<any>} Parsed JSON payload.
   */
  async #parseCartResponse(response) {
    const responseText = await response.text();
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      throw new Error(`Cart change request failed (${response.status})`);
    }

    if (!contentType.includes('application/json')) {
      throw new Error('Cart change response was not JSON');
    }

    return JSON.parse(responseText);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      void this.#rerenderSectionFromServer({ mode: 'full' });
      return;
    }
    if (event.target === this) return;

    // Shopify may still mutate the cart on error (e.g. qty capped at max). Clear
    // pending chrome, then fall through so we morph or re-fetch like a normal update.
    if (event.detail?.data?.didError) {
      this.#clearPendingAdd();
    }

    const sectionPayload = event.detail.data.sections?.[this.sectionId];
    const cartItemsHtml = typeof sectionPayload === 'string' ? sectionPayload : null;
    if (cartItemsHtml) {
      this.#applySectionHtml(cartItemsHtml);
      this.#setPendingBusy(false);
      this.#syncDrawerEmptyClass();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      this.#setPendingBusy(false);
      if (this.#emptyRevealPending) return;
      // Cart/add can return sections[id]=null for a stale theme section id; Section
      // Rendering then 404s. Catch so morph failures stay off the uncaught channel.
      void this.#rerenderSectionFromServer();
    }
  };

  /**
   * Re-fetch this component's section via the Section Rendering API.
   * @param {{ mode?: 'hydration'|'full' }} [options]
   */
  async #rerenderSectionFromServer(options = {}) {
    const mode = options.mode ?? (this.isDrawer ? 'hydration' : 'full');

    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
      this.#syncDrawerEmptyClass();
    } catch (error) {
      console.warn('[cart-items] section re-render failed', this.sectionId, error);
    }
  }

  /** Drop last-line empty stash so a failed `cart/change` cannot skip later morphs. */
  #clearEmptyRevealStash() {
    this.#emptyRevealPending = false;
    this.#stashedSectionHtml = null;
  }

  /**
   * Drawer uses hydration so an open `<dialog>` is not replaced (full morph closes it).
   * @param {string | undefined} html
   */
  #applySectionHtml(html) {
    if (!html) return;
    if (this.#emptyRevealPending) {
      this.#stashedSectionHtml = html;
      return;
    }
    morphSection(this.sectionId, html, this.isDrawer ? 'hydration' : 'full');
    this.#syncDrawerEmptyClass();
  }

  #applyStashedSection() {
    if (!this.#stashedSectionHtml) return;
    const html = this.#stashedSectionHtml;
    this.#stashedSectionHtml = null;
    morphSection(this.sectionId, html, this.isDrawer ? 'hydration' : 'full');
    this.#syncDrawerEmptyClass();
  }

  /**
   * Hydration morphs `cart-drawer-inner` only; `cart-drawer--empty` on `<dialog>` is not updated.
   */
  #syncDrawerEmptyClass() {
    if (!this.isDrawer) return;
    this.closest('dialog')?.classList.toggle('cart-drawer--empty', !this.querySelector('.cart-form'));
  }

  /**
   * @param {Event} event
   */
  #handleAddPending = (event) => {
    const variantId = event.detail?.data?.variantId?.toString();
    const existing = variantId
      ? this.querySelector(`cart-quantity-selector-component[data-variant-id="${variantId}"]`)
      : null;
    if (existing instanceof HTMLElement) {
      const row = existing.closest('.cart-items__table-row');
      const textComponent = /** @type {TextComponent | undefined} */ (row?.querySelector('text-component'));
      textComponent?.shimmer();
      this.refs.cartTotal?.shimmer();
      this.#setPendingBusy(true);
      return;
    }

    this.#insertSkeletonRow();
    this.#setPendingBusy(true);
  };

  #handleAddError = () => {
    this.#clearPendingAdd();
  };

  #insertSkeletonRow() {
    const skeletonTemplate = this.#getTemplate('cart-line-skeleton-template');
    if (!skeletonTemplate) return;

    let tbody = this.querySelector('.cart-items__table tbody');
    if (!tbody) {
      this.#mountPendingShell();
      tbody = this.querySelector('.cart-items__table tbody');
    }
    if (!tbody) return;

    const row = skeletonTemplate.content.firstElementChild?.cloneNode(true);
    if (!(row instanceof HTMLElement)) return;
    tbody.prepend(row);
  }

  #mountPendingShell() {
    const shellTemplate = this.#getTemplate('cart-items-pending-shell-template');
    const wrapper = this.querySelector('.cart-items__wrapper');
    if (!shellTemplate || !wrapper) return;

    this.#revealPendingDrawerChrome(shellTemplate.dataset.cartTitle);
    this.classList.add('cart-items--pending');
    wrapper.replaceChildren(document.importNode(shellTemplate.content, true));
  }

  /**
   * Empty drawer keeps "Your cart is empty" chrome unless we flip it to the filled layout.
   * @param {string | undefined} cartTitle
   */
  #revealPendingDrawerChrome(cartTitle) {
    if (!this.isDrawer) return;

    const dialog = this.closest('dialog');
    dialog?.classList.remove('cart-drawer--empty');

    const header = this.querySelector('.cart-drawer__header');
    const heading = this.querySelector('.cart-drawer__heading');
    if (heading instanceof HTMLElement) {
      heading.classList.remove('cart-drawer__heading--empty');
      heading.id = 'cart-drawer-heading';
      if (header instanceof HTMLElement && !header.contains(heading)) {
        header.prepend(heading);
      }

      let label = heading.querySelector('.cart-drawer__heading-label');
      if (!(label instanceof HTMLElement)) {
        label = document.createElement('span');
        label.className = 'cart-drawer__heading-label';
        heading.prepend(label);
      }
      if (cartTitle) label.textContent = cartTitle;

      dialog?.setAttribute('aria-labelledby', heading.id);
    }

    this.querySelector('.cart-items__empty-button')?.setAttribute('hidden', '');
  }

  /**
   * Prefer the live snippet template over the inert copy inside `#empty-cart-template`.
   * @param {string} id
   * @returns {HTMLTemplateElement | null}
   */
  #getTemplate(id) {
    const local = this.querySelector(`#${id}`);
    if (local instanceof HTMLTemplateElement) return local;
    const global = document.getElementById(id);
    return global instanceof HTMLTemplateElement ? global : null;
  }

  #clearPendingAdd() {
    this.querySelectorAll('[data-cart-line-skeleton]').forEach((el) => el.remove());
    this.classList.remove('cart-items--pending');
    resetShimmer(this);
    this.#setPendingBusy(false);

    const realRows = this.querySelectorAll('tbody .cart-items__table-row:not([data-cart-line-skeleton])');
    if (realRows.length === 0) this.#restoreEmpty();
  }

  #restoreEmpty() {
    if (this.isDrawer) {
      this.closest('dialog')?.classList.add('cart-drawer--empty');
      const template = document.getElementById('empty-cart-template');
      if (template instanceof HTMLTemplateElement) {
        this.replaceChildren(document.importNode(template.content, true));
        return;
      }
    }

    void this.#rerenderSectionFromServer({ mode: 'full' });
  }

  /**
   * @param {boolean} busy
   */
  #setPendingBusy(busy) {
    const status =
      this.querySelector('[data-cart-pending-status]') ||
      this.#ensurePendingStatus();
    const label = this.#getTemplate('cart-line-skeleton-template')?.getAttribute('data-status') || '';

    if (busy) {
      this.setAttribute('aria-busy', 'true');
      if (status) status.textContent = label;
      return;
    }

    this.removeAttribute('aria-busy');
    if (status) status.textContent = '';
  }

  /** @returns {HTMLElement | null} */
  #ensurePendingStatus() {
    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.dataset.cartPendingStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    this.prepend(status);
    return status;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
